import { useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import {
  saveMenuCategory,
  updateMenuCategory,
  deleteMenuCategory,
  updateCategories,
  updateAllCategories,
  saveMenuItem,
  saveMenuItems,
  saveMenuGroup,
  updateMenuGroup,
  deleteMenuGroup,
  saveMenuGroupOption,
  saveMenuGroupOptions,
  updateMenu,
  UPDATE_MENU_GROUP_SUCCESS,
} from 'store/actions/restaurantMenu';
import { moveMenuItemToCategory } from 'views/MenuCategory/components/MenuItem/moveMenuItem';

import {
  categoriesInOrder,
  categoriesWithoutGroup,
  copiedGroupOptions,
  copiedGroupPayload,
  duplicateDishPayload,
  itemsWithDish,
  menuWithPromotionTags,
  newGroupSort,
  newGroupPayload,
} from './menuEditorPayloads';

const ok = (result) => !!(result && result.ok);

/**
 * Every write the editor makes. Each call resolves to true when the server accepted it; the thunks
 * show the success or error snackbar themselves. Where a thunk leaves Redux stale, this fills the gap
 * locally so the editor always shows what was saved.
 */
const useMenuEditorActions = (menuId) => {
  const dispatch = useDispatch();
  const store = useStore();

  return useMemo(() => {
    const state = () => store.getState().restaurantMenu;
    const categoryById = (categoryId) => state().menuCategories.find((category) => category.id === categoryId);
    const groupById = (groupId) => state().menuGroups.find((group) => group.id === groupId);

    const saveItems = async (payload) => ok(await dispatch(saveMenuItems(payload)));

    // The thunk only sends the request; keep the store in step after the server accepts it.
    const saveGroupOptions = async (group, options) => {
      const saved = ok(await dispatch(saveMenuGroupOptions({ options, menuId, groupId: group.id })));
      if (saved) {
        dispatch({ type: UPDATE_MENU_GROUP_SUCCESS, payload: { ...group, options } });
      }
      return saved;
    };

    return {
      // ---- categories
      createCategory: async (payload) => ok(await dispatch(saveMenuCategory(payload))),
      updateCategory: async (payload) => ok(await dispatch(updateMenuCategory(payload))),
      deleteCategory: async (categoryId) => {
        const deleted = ok(await dispatch(deleteMenuCategory(menuId, categoryId)));
        if (deleted) {
          dispatch(updateCategories(state().menuCategories.filter((category) => category.id !== categoryId)));
        }
        return deleted;
      },
      reorderCategories: async (ordered, from, to) => {
        const next = categoriesInOrder(ordered, from, to);
        dispatch(updateCategories(next));
        return ok(await dispatch(updateAllCategories({ menuId, categories: next })));
      },

      // ---- dishes
      saveItems,
      // Resolves to the new dish's id, or false.
      createDish: async (payload) => {
        const result = await dispatch(saveMenuItem(payload));
        return ok(result) ? (result.data && result.data.id) || true : false;
      },
      updateDish: async (dish) => {
        const category = categoryById(dish.categoryId);
        if (!category) {
          return false;
        }
        return saveItems(itemsWithDish(category, dish));
      },
      duplicateDish: async (dish) => ok(await dispatch(saveMenuItem(duplicateDishPayload(dish)))),
      moveDish: async (dish, targetCategoryId) => {
        const next = moveMenuItemToCategory(state().menuCategories, dish, targetCategoryId);
        if (!next) {
          return true;
        }
        dispatch(updateCategories(next));
        return ok(await dispatch(updateAllCategories({ menuId: dish.menuId || menuId, categories: next })));
      },
      addMenuPromotionTags: async (tags) => {
        const menu = state().menu;
        if (!menu || menu.id !== menuId) {
          // The store can still hold a menu opened earlier; never merge tags into the wrong one.
          return false;
        }
        const missing = tags.filter((tag) => !((menu && menu.promotionTags) || []).includes(tag));
        if (missing.length === 0) {
          return true;
        }
        return ok(await dispatch(updateMenu(menuWithPromotionTags(menu, tags), menuId)));
      },

      // ---- choice groups
      createGroup: async (form) =>
        ok(await dispatch(saveMenuGroup(newGroupPayload(form, menuId, newGroupSort(state().menuGroups))))),
      updateGroup: async (payload) => ok(await dispatch(updateMenuGroup(payload))),
      copyGroup: async (group) => {
        const result = await dispatch(saveMenuGroup(copiedGroupPayload(group)));
        if (!ok(result)) {
          return false;
        }
        // POST /menuGroup always stores an empty option list, so the options follow in a second call.
        const options = copiedGroupOptions(group, result.data.id);
        if (options.length === 0) {
          return true;
        }
        return saveGroupOptions(groupById(result.data.id) || { ...copiedGroupPayload(group), id: result.data.id }, options);
      },
      deleteGroup: async (groupId) => {
        const { all, changed } = categoriesWithoutGroup(state().menuCategories, groupId);
        if (changed.length > 0) {
          dispatch(updateCategories(all));
          if (!ok(await dispatch(updateAllCategories({ menuId, categories: changed })))) {
            return false;
          }
        }
        return ok(await dispatch(deleteMenuGroup(menuId, groupId)));
      },
      createOption: async (payload) => ok(await dispatch(saveMenuGroupOption(payload))),
      saveGroupOptions,
    };
  }, [dispatch, store, menuId]);
};

export default useMenuEditorActions;
